import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Table, Typography } from 'antd';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { type MobilityKind } from '../../../../api/types';
import TranslatedInputField from '../../../../components/fields/TranslatedInputField';
import { type MobilityFormValues } from './types';
import { TitleAndActionContainer } from './ui';

type AdditionalInfoDetailsFieldArrayProps = {
    name?: string;
    disabled?: boolean;
};

type FieldValue = Extract<MobilityFormValues, { kind: MobilityKind.AdditionalInfo }>['detail'];

const AdditionalInfoDetailsFieldArray = ({
    name = 'detail',
    disabled = false,
}: AdditionalInfoDetailsFieldArrayProps) => {
    const { t } = useTranslation('mobilityDetails');

    const [{ value }] = useField<FieldValue>(name);

    return (
        <FieldArray
            name={name}
            render={({ remove, push }) => {
                const renderActions = (index: number) =>
                    !disabled && (
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                remove(index);
                            }}
                            size="middle"
                            type="link"
                        />
                    );

                return (
                    <>
                        <TitleAndActionContainer>
                            <Typography.Title level={5} style={{ fontSize: 14 }}>
                                {t('mobilityDetails:fields.detail.label')}
                            </Typography.Title>
                        </TitleAndActionContainer>
                        <Table
                            dataSource={value}
                            pagination={false}
                            rowKey={(row, index: number) => `${name}_${index.toString()}`}
                        >
                            <Table.Column
                                dataIndex="subTitle"
                                render={(value, record, index) => (
                                    <TranslatedInputField disabled={disabled} name={`${name}[${index}].subTitle`} />
                                )}
                                title={t('mobilityDetails:fields.detailSubTitle.label')}
                            />
                            <Table.Column
                                dataIndex="description"
                                render={(value, record, index) => (
                                    <TranslatedInputField disabled={disabled} name={`${name}[${index}].description`} />
                                )}
                                title={t('mobilityDetails:fields.detailDescription.label')}
                            />
                            <Table.Column key="actions" align="center" render={renderActions} width={100} />
                        </Table>
                        <div style={{ marginTop: '10px' }}>
                            {!disabled && (
                                <Button
                                    icon={<PlusCircleOutlined />}
                                    onClick={() =>
                                        push({
                                            subTitle: { defaultValue: '', overrides: [] },
                                            description: { defaultValue: '', overrides: [] },
                                        })
                                    }
                                    type="link"
                                >
                                    {t('mobilityDetails:actions.addDetail')}
                                </Button>
                            )}
                        </div>
                    </>
                );
            }}
        />
    );
};

export default AdditionalInfoDetailsFieldArray;
